import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WorkflowBuilder } from '@/components/WorkflowBuilder';
import { EnhancedWorkflowBuilder } from '@/components/EnhancedWorkflowBuilder';
import { Calendar } from '@/components/Calendar';
import { WorkflowExecutionMonitor } from '@/components/WorkflowExecutionMonitor';
import { ProjectLifecyclePanel } from '@/components/ProjectLifecyclePanel';
import { 
  workflowExecutionEngine, 
  QUICK_START_TEMPLATES,
  type WorkflowDefinition 
} from '@/services/workflowExecutionEngine';
import { blink } from '@/lib/blink';
import { toast } from 'sonner';
import { workflowCacheService } from '@/services/workflowCacheService';
import { safeJsonParse } from '@/lib/utils';
import {
  Plus,
  Play,
  Pause,
  Edit,
  Trash2,
  Zap,
  Mail,
  Filter,
  Tag,
  Clock,
  ArrowRight,
  Settings,
  Copy,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  GitBranch,
  CalendarDays,
  Workflow as WorkflowIcon,
  Sparkles,
  Brain,
} from 'lucide-react';

interface Workflow {
  id: number;
  name: string;
  description: string;
  trigger: string;
  actions: string[];
  status: 'active' | 'paused' | 'draft';
  lastRun: string;
  runsToday: number;
  successRate: number;
  icon: any;
  color: string;
}

const workflows: Workflow[] = [
  {
    id: 1,
    name: 'Auto-Reply to Urgent Emails',
    description: 'Automatically sends acknowledgment for high-priority emails',
    trigger: 'Email marked as urgent',
    actions: ['Analyze content', 'Generate reply', 'Send acknowledgment'],
    status: 'active',
    lastRun: '10 mins ago',
    runsToday: 23,
    successRate: 98,
    icon: Zap,
    color: 'text-yellow-500',
  },
  {
    id: 2,
    name: 'Newsletter Organizer',
    description: 'Categorizes and archives newsletters automatically',
    trigger: 'Newsletter received',
    actions: ['Identify newsletter', 'Apply label', 'Archive after 7 days'],
    status: 'active',
    lastRun: '1 hour ago',
    runsToday: 45,
    successRate: 100,
    icon: Mail,
    color: 'text-blue-500',
  },
  {
    id: 3,
    name: 'Meeting Request Handler',
    description: 'Extracts meeting details and creates calendar events',
    trigger: 'Meeting invitation detected',
    actions: ['Extract details', 'Check calendar', 'Create event', 'Send confirmation'],
    status: 'paused',
    lastRun: '2 days ago',
    runsToday: 0,
    successRate: 95,
    icon: Clock,
    color: 'text-green-500',
  },
  {
    id: 4,
    name: 'Smart Email Tagger',
    description: 'AI-powered email classification and tagging',
    trigger: 'New email received',
    actions: ['Analyze content', 'Classify category', 'Apply tags', 'Update priority'],
    status: 'active',
    lastRun: '5 mins ago',
    runsToday: 187,
    successRate: 96,
    icon: Tag,
    color: 'text-purple-500',
  },
];

const workflowTemplates = [
  {
    name: 'Follow-up Reminder',
    description: 'Send reminders for emails without replies',
    icon: Clock,
  },
  {
    name: 'Attachment Saver',
    description: 'Save email attachments to cloud storage',
    icon: Copy,
  },
  {
    name: 'Out of Office Auto-Reply',
    description: 'Automatic responses when away',
    icon: Mail,
  },
  {
    name: 'VIP Email Alert',
    description: 'Get notified for important contacts',
    icon: AlertCircle,
  },
];

export default function Workflows() {
  const [activeWorkflows, setActiveWorkflows] = useState(workflows);
  const [savedWorkflows, setSavedWorkflows] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState<any>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  const [workflowToLoad, setWorkflowToLoad] = useState<string | null>(null);

  // Load user and initialize workflows using auth state listener with caching
  useEffect(() => {
    const loadWorkflows = async (userId: string) => {
      try {
        // Check cache first
        const cached = workflowCacheService.get(userId);
        if (cached) {
          setSavedWorkflows(cached);
          console.log(`✅ Loaded ${cached.length} workflows from cache`);
          return;
        }

        // Cache miss - fetch from database
        const dbWorkflows = await blink.db.workflows.list({
          where: { userId },
          orderBy: { updatedAt: 'desc' }
        });
        
        if (dbWorkflows && dbWorkflows.length > 0) {
          setSavedWorkflows(dbWorkflows);
          // Update cache
          workflowCacheService.set(userId, dbWorkflows);
          console.log(`✅ Loaded ${dbWorkflows.length} workflows from DB and cached`);
        } else {
          setSavedWorkflows([]);
          // Cache empty result to avoid repeated DB calls
          workflowCacheService.set(userId, []);
        }
      } catch (error) {
        console.error('Failed to load workflows:', error);
        // Don't clear workflows on error - keep cached data
      }
    };

    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      if (state.user && !state.isLoading) {
        setUser(state.user);
        console.log('✅ User authenticated for workflows:', state.user.id);
        await loadWorkflows(state.user.id);
      } else if (!state.isLoading && !state.user) {
        console.log('⚠️ User not authenticated');
        setUser(null);
        setSavedWorkflows([]);
      }
    });
    
    return () => unsubscribe();
  }, []);

  const toggleWorkflow = (id: number) => {
    setActiveWorkflows((prev) =>
      prev.map((w) =>
        w.id === id
          ? { ...w, status: w.status === 'active' ? 'paused' : 'active' }
          : w
      )
    );
  };

  /**
   * Load and execute Quick Start Template
   */
  const loadQuickStartTemplate = async (templateIndex: number) => {
    if (!user) {
      toast.error('Please sign in to use workflow templates');
      return;
    }

    setIsLoadingTemplate(true);
    console.log(`🚀 Loading Quick Start Template ${templateIndex + 1}`);

    try {
      // Get template and set userId
      const template = { ...QUICK_START_TEMPLATES[templateIndex] };
      template.userId = user.id;
      template.id = `${template.id}_${Date.now()}`;

      // Register workflow with execution engine
      workflowExecutionEngine.registerWorkflow(template);
      console.log(`✅ Registered workflow: ${template.name}`);

      // Show success with action options
      toast.success(`Workflow "${template.name}" loaded successfully!`, {
        description: 'Workflow is now active and monitoring for triggers.',
        duration: 4000
      });

      // Update UI to show workflow is active
      setActiveWorkflows(prev => [...prev, {
        id: Date.now(),
        name: template.name,
        description: template.description,
        trigger: template.nodes[0].title,
        actions: template.nodes.slice(1).map(n => n.title),
        status: 'active',
        lastRun: 'Just now',
        runsToday: 0,
        successRate: 100,
        icon: template.name.includes('Reply') ? Zap : 
              template.name.includes('Newsletter') ? Mail :
              template.name.includes('Meeting') ? Clock : Tag,
        color: 'text-primary'
      }]);

      // For demo: trigger the workflow immediately with mock data
      if (template.name === 'Auto-Reply to Urgent Emails') {
        await demoTriggerAutoReply(template);
      } else if (template.name === 'Smart Email Tagger') {
        await demoTriggerSmartTagger(template);
      }

    } catch (error) {
      console.error('Failed to load template:', error);
      toast.error('Failed to load workflow template');
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  /**
   * Demo: Trigger Auto-Reply workflow
   */
  const demoTriggerAutoReply = async (workflow: WorkflowDefinition) => {
    console.log('📧 Demo: Triggering auto-reply workflow with urgent email');
    
    try {
      const executionIds = await workflowExecutionEngine.triggerWorkflow({
        type: 'email_received',
        data: {
          id: 'demo_email_1',
          from: 'urgent@example.com',
          subject: 'URGENT: Server Down',
          body: 'Our production server is experiencing critical issues. Please respond ASAP.',
          snippet: 'Server issues need immediate attention',
          isUrgent: true,
          receivedDate: new Date().toISOString()
        },
        timestamp: new Date()
      });

      if (executionIds.length > 0) {
        // Show execution monitor
        setCurrentExecutionId(executionIds[0]);
        
        toast.success('Auto-reply workflow triggered!', {
          description: 'Processing urgent email and generating response...',
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Demo trigger failed:', error);
    }
  };

  /**
   * Demo: Trigger Smart Tagger workflow
   */
  const demoTriggerSmartTagger = async (workflow: WorkflowDefinition) => {
    console.log('🏷️ Demo: Triggering smart tagger workflow');
    
    try {
      const executionIds = await workflowExecutionEngine.triggerWorkflow({
        type: 'email_received',
        data: {
          id: 'demo_email_2',
          from: 'marketing@example.com',
          subject: 'New Product Launch - Q1 2024',
          body: 'Exciting news about our new product line launching next quarter...',
          snippet: 'Product launch announcement',
          isUrgent: false,
          receivedDate: new Date().toISOString()
        },
        timestamp: new Date()
      });

      if (executionIds.length > 0) {
        // Show execution monitor
        setCurrentExecutionId(executionIds[0]);
        
        toast.success('Smart tagging workflow triggered!', {
          description: 'Analyzing email and applying tags...',
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Demo trigger failed:', error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Execution Monitor - Floating */}
      {currentExecutionId && (
        <div className="fixed bottom-4 right-4 w-96 z-50">
          <WorkflowExecutionMonitor 
            executionId={currentExecutionId} 
            onClose={() => setCurrentExecutionId(null)}
          />
        </div>
      )}
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Email Workflows & Calendar</h1>
          <p className="text-muted-foreground mt-1">
            Automate your email management with intelligent workflows and scheduling
          </p>
        </div>
        <Button className="bg-primary hover:bg-primary/90" onClick={() => setActiveTab('builder')}>
          <Plus className="mr-2 h-4 w-4" />
          Create Workflow
        </Button>
      </div>

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="glass-card">
          <TabsTrigger value="overview" className="gap-2">
            <GitBranch className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="builder" className="gap-2">
            <WorkflowIcon className="w-4 h-4" />
            Flow Builder
          </TabsTrigger>
          <TabsTrigger value="lifecycle" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            Project Lifecycle
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2">
            <CalendarDays className="w-4 h-4" />
            Calendar
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-0">

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Workflows</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">3</div>
            <p className="text-xs text-muted-foreground">of 4 total</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Runs Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">255</div>
            <p className="text-xs text-green-500 flex items-center">
              <TrendingUp className="h-3 w-3 mr-1" />
              +18% from yesterday
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">97.2%</div>
            <Progress value={97.2} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Time Saved</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">4.5h</div>
            <p className="text-xs text-muted-foreground">this week</p>
          </CardContent>
        </Card>
      </div>

      {/* Active Workflows */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Your Workflows</h2>
          <Button variant="outline" size="sm" onClick={() => setActiveTab('builder')}>
            <Plus className="w-4 h-4 mr-2" />
            Create Workflow
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...activeWorkflows, ...savedWorkflows.map(wf => {
            let parsedActions = [];
            try {
              parsedActions = safeJsonParse(wf.actions);
            } catch (e) {
              console.warn('Failed to parse workflow actions:', wf.id, e);
              parsedActions = [];
            }
            
            return {
              id: wf.id, // Keep original ID - will be string like 'wf_abc123'
              name: wf.name,
              description: wf.description,
              trigger: wf.trigger,
              actions: parsedActions.slice(1).map((n: any) => n.title || 'Untitled'),
              status: Number(wf.isActive) > 0 ? 'active' : 'paused',
              lastRun: wf.lastRunAt || 'Never',
              runsToday: 0,
              successRate: 100,
              icon: WorkflowIcon,
              color: 'text-primary',
              _isSaved: true // Mark as saved workflow for easier identification
            };
          })].map((workflow) => (
            <Card key={workflow.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg bg-background ${workflow.color}`}>
                      <workflow.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{workflow.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {workflow.description}
                      </CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={workflow.status === 'active'}
                    onCheckedChange={() => toggleWorkflow(workflow.id)}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Trigger */}
                  <div className="flex items-center gap-2 text-sm">
                    <GitBranch className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Trigger:</span>
                    <span>{workflow.trigger}</span>
                  </div>

                  {/* Actions Flow */}
                  <div className="flex items-center gap-1 text-xs">
                    {workflow.actions.map((action, index) => (
                      <div key={index} className="flex items-center gap-1">
                        <Badge variant="secondary" className="text-xs">
                          {action}
                        </Badge>
                        {index < workflow.actions.length - 1 && (
                          <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        <span className="text-muted-foreground">
                          {workflow.runsToday} runs today
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        <span className="text-muted-foreground">{workflow.lastRun}</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => {
                          console.log('🔧 Edit clicked for workflow:', workflow.id, 'Type:', typeof workflow.id);
                          
                          // Check if this is a saved workflow (has wf_ prefix or _isSaved marker)
                          const isSavedWorkflow = (typeof workflow.id === 'string' && workflow.id.startsWith('wf_')) || 
                                                   (workflow as any)._isSaved;
                          
                          if (isSavedWorkflow) {
                            // Pass workflow ID to builder and switch tabs
                            console.log('✅ Loading workflow for edit:', workflow.id);
                            setWorkflowToLoad(workflow.id.toString());
                            setActiveTab('builder');
                            toast.success('Loading workflow for editing...');
                          } else {
                            console.log('❌ Cannot edit - not a saved workflow:', workflow.id);
                            toast.error('Cannot edit demo workflows');
                          }
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          if (!user) {
                            toast.error('Please sign in to delete workflows');
                            return;
                          }
                          
                          console.log('🗑️ Delete clicked for workflow:', workflow.id, 'Type:', typeof workflow.id);
                          
                          // Check if this is a saved workflow (has wf_ prefix or _isSaved marker)
                          const isSavedWorkflow = (typeof workflow.id === 'string' && workflow.id.startsWith('wf_')) || 
                                                   (workflow as any)._isSaved;
                          
                          if (isSavedWorkflow) {
                            const confirmed = window.confirm(`Delete workflow "${workflow.name}"?\n\nThis action cannot be undone.`);
                            if (confirmed) {
                              try {
                                console.log('🔄 Deleting workflow:', workflow.id);
                                
                                // Optimistic update - remove from UI immediately
                                workflowCacheService.removeWorkflow(user.id, workflow.id.toString());
                                setSavedWorkflows(prev => prev.filter(w => w.id !== workflow.id));
                                
                                // Delete from database
                                await blink.db.workflows.delete(workflow.id.toString());
                                toast.success(`Workflow "${workflow.name}" deleted successfully`);
                                
                                console.log(`✅ Deleted workflow: ${workflow.name} (${workflow.id})`);
                              } catch (error) {
                                console.error('❌ Failed to delete workflow:', error);
                                toast.error('Failed to delete workflow. Please try again.');
                                
                                // Revert optimistic update on error
                                workflowCacheService.invalidate(user.id);
                                const dbWorkflows = await blink.db.workflows.list({
                                  where: { userId: user.id },
                                  orderBy: { updatedAt: 'desc' }
                                });
                                setSavedWorkflows(dbWorkflows || []);
                                workflowCacheService.set(user.id, dbWorkflows || []);
                              }
                            } else {
                              console.log('❌ Delete cancelled by user');
                            }
                          } else {
                            console.log('🎭 Demo workflow - removing from UI only');
                            // Demo workflow - just remove from UI
                            setActiveWorkflows(prev => prev.filter(w => w.id !== workflow.id));
                            toast.success('Demo workflow removed');
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Workflow Templates - LIVE with Real Implementation */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              Quick Start Templates
              <Badge variant="default" className="gap-1">
                <Sparkles className="w-3 h-3" />
                Live
              </Badge>
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              AI-powered workflows ready to use - click to activate instantly
            </p>
          </div>
          <Button variant="outline" size="sm">
            View All Templates
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {QUICK_START_TEMPLATES.map((template, index) => {
            const IconComponent = template.name.includes('Reply') ? Zap : 
                                  template.name.includes('Newsletter') ? Mail :
                                  template.name.includes('Meeting') ? Clock : Tag;
            
            return (
              <Card
                key={index}
                className="hover:shadow-lg transition-all cursor-pointer hover:border-primary hover:scale-105 relative overflow-hidden"
              >
                <div className="absolute top-2 right-2">
                  <Badge variant="secondary" className="text-xs gap-1">
                    <Brain className="w-3 h-3" />
                    AI
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 text-primary">
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-sm">{template.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {template.description}
                      </p>
                      <Badge variant="outline" className="mt-2 text-xs">
                        {template.nodes.length} steps
                      </Badge>
                    </div>
                  </div>
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="w-full mt-3 gap-2"
                    onClick={() => loadQuickStartTemplate(index)}
                    disabled={isLoadingTemplate}
                  >
                    {isLoadingTemplate ? (
                      <>
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        Activate Now
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border-primary/20">
          <CardContent className="p-4 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-primary" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                🔥 Live AI-Powered Workflows
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                These templates use GPT-5 Nano Agent with real Microsoft Graph API calls. 
                Click "Activate Now" to start using them immediately.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Builder Preview */}
      <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Visual Workflow Builder
          </CardTitle>
          <CardDescription>
            Create complex email automations with our drag-and-drop workflow builder
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8 border-2 border-dashed border-primary/20 rounded-lg">
            <div className="text-center">
              <GitBranch className="h-12 w-12 text-primary mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-4">
                Design powerful email workflows visually
              </p>
              <Button onClick={() => setActiveTab('builder')}>Open Workflow Builder</Button>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Flow Builder Tab */}
        <TabsContent value="builder" className="mt-0">
          <EnhancedWorkflowBuilder 
            workflowIdToLoad={workflowToLoad} 
            onWorkflowLoaded={() => setWorkflowToLoad(null)}
          />
        </TabsContent>

        {/* Project Lifecycle Tab */}
        <TabsContent value="lifecycle" className="mt-0">
          <ProjectLifecyclePanel />
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="mt-0">
          <Calendar />
        </TabsContent>
      </Tabs>
    </div>
  );
}